import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, money } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import FlowDesigner from "@/components/FlowDesigner";
import TestCallModal from "@/components/TestCallModal";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Megaphone, Plus, Minus, Stop, FlowArrow, Sparkle, Phone, Archive, ArrowCounterClockwise, Copy, PencilSimple, Play, Trash, HardDrives } from "@phosphor-icons/react";
import AsteriskWizard from "@/components/AsteriskWizard";

// Default AMD settings for the New Campaign (create) dialog.
const AMD_CREATE_DEFAULTS = { enabled: false, timeout: "3200", action: "hangup", greetingMax: "4500", vmAudioId: "", vmFilename: "" };

// Flow-health chip so an incomplete/empty flow is obvious before a campaign
// takes a call. green = ready · amber = incomplete (dead-end/dangling) · grey = no flow yet.
const FLOW_HEALTH = {
  ready: { label: "Flow ready", cls: "bg-emerald-100 text-emerald-700 border border-emerald-200", title: "Flow passes validation — safe to take calls." },
  incomplete: { label: "Flow incomplete", cls: "bg-amber-100 text-amber-700 border border-amber-200", title: "Flow has a dead-end/dangling node and would drop calls. Open Design flow to fix." },
  empty: { label: "No flow", cls: "bg-muted text-muted-foreground border border-border", title: "No flow designed yet. Open Design flow to build one." },
};
function FlowHealthBadge({ health, id }) {
  const h = FLOW_HEALTH[health] || FLOW_HEALTH.empty;
  return (
    <span data-testid={`campaign-flow-health-${id}`} title={h.title}
      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full ${h.cls}`}>
      {h.label}
    </span>
  );
}

export default function Campaigns() {
  const { user } = useAuth();
  const isCustomer = user?.role === "customer";
  const [campaigns, setCampaigns] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [assigned, setAssigned] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [browse, setBrowse] = useState(false);
  const [name, setName] = useState("");
  const [opensipsId, setOpensipsId] = useState("__none__");
  const [vicidialId, setVicidialId] = useState("");
  const [maxDur, setMaxDur] = useState("");
  const [defaultOps, setDefaultOps] = useState(null);
  const [defaultVici, setDefaultVici] = useState(null);
  const [vicidial, setVicidial] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [designer, setDesigner] = useState(null);
  const [savingFlow, setSavingFlow] = useState(false);
  const [testCall, setTestCall] = useState(null);
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [vmUploading, setVmUploading] = useState(false);
  const vmInputRef = useRef(null);
  // AMD settings for the New Campaign (create) dialog
  const [createAmd, setCreateAmd] = useState(AMD_CREATE_DEFAULTS);
  const [createVmUploading, setCreateVmUploading] = useState(false);
  const createVmInputRef = useRef(null);
  const [confirm, setConfirm] = useState(null); // { action: "copy"|"delete", camp }
  const [asterisk, setAsterisk] = useState(null); // campaign for the Asterisk wizard

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/campaigns", { params: { include_archived: showArchived } })];
      if (isCustomer) { reqs.push(api.get("/servers/assigned"), api.get("/templates")); }
      const [c, srv, tpl] = await Promise.all(reqs);
      setCampaigns(c.data);
      if (isCustomer) { setAssigned(srv.data.opensips || []); setVicidial(srv.data.vicidial || []); setDefaultOps(srv.data.default_opensips_id || null); setDefaultVici(srv.data.default_vicidial_id || null); setTemplates(tpl.data); }
    } finally { setLoading(false); }
  }, [isCustomer, showArchived]);
  useEffect(() => { load(); }, [load]);
  // Keep the live "Active Calls" column fresh (silent refresh, no spinner flicker).
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/campaigns", { params: { include_archived: showArchived } });
        setCampaigns(data);
      } catch { /* ignore transient polling errors */ }
    }, 10000);
    return () => clearInterval(t);
  }, [showArchived]);
  // Preselect the platform default OpenSIPS/VICIdial (only if assigned + active) when opening the dialog.
  useEffect(() => { if (open) { setOpensipsId(defaultOps || "__none__"); setVicidialId(defaultVici || ""); setCreateAmd(AMD_CREATE_DEFAULTS); } }, [open, defaultOps, defaultVici]);

  const create = async () => {
    if (!name.trim()) { toast.error("Enter a campaign name"); return; }
    if (!vicidialId) { toast.error("Select a VICIdial server"); return; }
    if (createAmd.enabled && createAmd.action === "voicemail" && !createAmd.vmAudioId) {
      toast.error("Upload a voicemail drop audio file for the Leave voicemail action"); return;
    }
    setSaving(true);
    try {
      const body = { name, vicidial_server_id: vicidialId };
      if (opensipsId !== "__none__") body.opensips_server_id = opensipsId;
      body.max_call_duration_sec = maxDur ? Number(maxDur) : null;
      body.amd_enabled = !!createAmd.enabled;
      body.amd_decision_timeout_ms = Number(createAmd.timeout) || 3200;
      body.amd_machine_action = createAmd.action;
      body.amd_greeting_max_ms = Number(createAmd.greetingMax) || 4500;
      body.amd_voicemail_audio_id = createAmd.vmAudioId || "";
      body.amd_voicemail_filename = createAmd.vmFilename || "";
      await api.post("/campaigns", body);
      toast.success("Campaign created");
      setOpen(false); setName(""); setOpensipsId("__none__"); setVicidialId(""); setMaxDur(""); setCreateAmd(AMD_CREATE_DEFAULTS); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const adoptTemplate = async (t) => {
    try { await api.post(`/templates/${t.id}/use`, {}); toast.success(`Created "${t.name}" copy`); setBrowse(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const stop = async (camp) => {
    setBusy(camp.id);
    try { await api.post(`/campaigns/${camp.id}/concurrency`, { concurrency: 0 }); toast.success("Campaign stopped & holds released"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const archive = async (camp) => {
    setBusy(camp.id);
    try { await api.post(`/campaigns/${camp.id}/archive`); toast.success("Campaign archived"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const restore = async (camp) => {
    setBusy(camp.id);
    try { await api.post(`/campaigns/${camp.id}/restore`); toast.success("Campaign restored"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const start = async (camp) => {
    setBusy(camp.id);
    try { await api.post(`/campaigns/${camp.id}/concurrency`, { concurrency: Math.max(1, camp.concurrency || 1) }); toast.success("Campaign started"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const duplicate = async (camp) => {
    setBusy(camp.id);
    try { await api.post(`/campaigns/${camp.id}/duplicate`); toast.success(`Copied "${camp.name}"`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const del = async (camp) => {
    setBusy(camp.id);
    try { await api.delete(`/campaigns/${camp.id}`); toast.success(`Deleted "${camp.name}"`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const runConfirm = async () => {
    const c = confirm; setConfirm(null);
    if (!c) return;
    if (c.action === "copy") await duplicate(c.camp);
    else if (c.action === "delete") await del(c.camp);
    else if (c.action === "archive") await archive(c.camp);
  };
  const openEdit = (camp) => setEditing({
    id: camp.id, name: camp.name,
    opensipsId: camp.opensips_server_id || "__none__",
    vicidialId: camp.vicidial_server_id || "",
    maxDur: camp.max_call_duration_sec ? String(camp.max_call_duration_sec) : "",
    amdEnabled: !!camp.amd_enabled,
    amdTimeout: camp.amd_decision_timeout_ms ? String(camp.amd_decision_timeout_ms) : "3200",
    amdAction: (camp.amd_machine_action && camp.amd_machine_action !== "log_only") ? camp.amd_machine_action : "hangup",
    amdGreetingMax: camp.amd_greeting_max_ms ? String(camp.amd_greeting_max_ms) : "4500",
    vmAudioId: camp.amd_voicemail_audio_id || "",
    vmFilename: camp.amd_voicemail_filename || "",
  });
  const saveEdit = async () => {
    if (!editing.name.trim()) { toast.error("Enter a campaign name"); return; }
    if (!editing.vicidialId) { toast.error("Select a VICIdial server"); return; }
    if (editing.amdEnabled && editing.amdAction === "voicemail" && !editing.vmAudioId) {
      toast.error("Upload a voicemail drop audio file for the Leave voicemail action"); return;
    }
    setSavingEdit(true);
    try {
      await api.put(`/campaigns/${editing.id}`, {
        name: editing.name,
        opensips_server_id: editing.opensipsId === "__none__" ? "" : editing.opensipsId,
        vicidial_server_id: editing.vicidialId,
        max_call_duration_sec: editing.maxDur ? Number(editing.maxDur) : 0,
        amd_enabled: !!editing.amdEnabled,
        amd_decision_timeout_ms: Number(editing.amdTimeout) || 3200,
        amd_machine_action: editing.amdAction,
        amd_greeting_max_ms: Number(editing.amdGreetingMax) || 4500,
        amd_voicemail_audio_id: editing.vmAudioId || "",
        amd_voicemail_filename: editing.vmFilename || "",
      });
      toast.success("Campaign updated"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  const uploadVoicemail = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setVmUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/audio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((ed) => ({ ...ed, vmAudioId: data.id, vmFilename: data.original_filename }));
      if (data.warning) toast.warning(data.warning);
      else toast.success("Voicemail audio uploaded (telephony-compatible)");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setVmUploading(false); e.target.value = ""; }
  };

  const uploadVoicemailCreate = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCreateVmUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/audio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCreateAmd((a) => ({ ...a, vmAudioId: data.id, vmFilename: data.original_filename }));
      if (data.warning) toast.warning(data.warning);
      else toast.success("Voicemail audio uploaded (telephony-compatible)");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCreateVmUploading(false); e.target.value = ""; }
  };

  const openDesigner = async (camp) => {
    const { data } = await api.get(`/campaigns/${camp.id}`);
    setDesigner({ campaign: data, flow: data.flow || { nodes: [], edges: [] } });
  };
  const saveFlow = async (flow, opts = {}) => {
    setSavingFlow(true);
    try {
      await api.put(`/campaigns/${designer.campaign.id}/flow`, { flow, draft: !!opts.draft });
      toast.success(opts.keepOpen ? "Draft saved — keep editing" : "Flow saved");
      if (opts.keepOpen) { setDesigner((d) => (d ? { ...d, flow } : d)); }
      else { setDesigner(null); load(); }
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingFlow(false); }
  };

  const title = user?.role === "admin" ? "Global Campaigns" : "Campaigns";

  return (
    <div>
      <PageHeader title={title} subtitle="Design a flow, then start the campaign. Concurrent-call limits are set per account.">
        <Button variant={showArchived ? "secondary" : "outline"} className="rounded-lg" onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived-btn">
          <Archive className="w-4 h-4 mr-1.5" weight="bold" /> {showArchived ? "Hide archived" : "Show archived"}
        </Button>
        {isCustomer && (
          <>
            <Button variant="outline" className="rounded-lg" onClick={() => setBrowse(true)} data-testid="browse-templates-btn">
              <Sparkle className="w-4 h-4 mr-1.5" weight="fill" /> Use Global Campaign
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-campaign-btn" className="rounded-lg"><Plus className="w-4 h-4 mr-1.5" weight="bold" /> New Campaign</Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] flex flex-col" data-testid="create-campaign-dialog">
                <DialogHeader className="shrink-0"><DialogTitle>Create campaign</DialogTitle>
                  <DialogDescription>Pick a VICIdial server assigned to you (a routing prefix is generated automatically), then design the flow and start it.</DialogDescription></DialogHeader>
                <div className="space-y-4 py-2 overflow-y-auto flex-1 pr-1 -mr-1">
                  <div className="space-y-2"><Label>Campaign name</Label>
                    <Input data-testid="campaign-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Q3 Outreach" /></div>
                  <div className="space-y-2">
                    <Label>VICIdial server <span className="text-destructive">*</span></Label>
                    <Select value={vicidialId} onValueChange={setVicidialId}>
                      <SelectTrigger data-testid="campaign-vicidial-select"><SelectValue placeholder="Select assigned VICIdial server" /></SelectTrigger>
                      <SelectContent>
                        {vicidial.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.host}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {vicidial.length === 0 && <p className="text-xs text-muted-foreground">No VICIdial servers assigned to you yet — ask your admin.</p>}
                    <p className="text-xs text-muted-foreground">A unique routing prefix is generated automatically after creation.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Telephony IP</Label>
                    <Select value={opensipsId} onValueChange={setOpensipsId}>
                      <SelectTrigger data-testid="campaign-opensips-select"><SelectValue placeholder="Select assigned server" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Unassigned (set later)</SelectItem>
                        {assigned.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.host}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {assigned.length === 0 && <p className="text-xs text-muted-foreground">No Telephony servers assigned to you yet — ask your admin.</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Max Call Duration (sec)</Label>
                    <Input data-testid="campaign-max-duration" type="number" min="1" max="3600" value={maxDur} onChange={(e) => setMaxDur(e.target.value)} placeholder="Unlimited" />
                    <p className="text-xs text-muted-foreground">Caps the AI bot ↔ customer call. Leave blank for no limit. Not applied after transfer to an agent.</p>
                  </div>

                  <div className="space-y-3 rounded-lg border border-border p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm">Answering Machine Detection</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Detect human vs voicemail on the calls VICIdial couldn't classify.</p>
                      </div>
                      <Switch data-testid="create-campaign-amd-enabled" checked={createAmd.enabled} onCheckedChange={(v) => setCreateAmd({ ...createAmd, enabled: v })} />
                    </div>
                    {createAmd.enabled && (
                      <div className="grid grid-cols-2 gap-3 pt-1">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Decision window (ms)</Label>
                          <Input data-testid="create-campaign-amd-timeout" type="number" min="1000" max="8000" step="100" value={createAmd.timeout} onChange={(e) => setCreateAmd({ ...createAmd, timeout: e.target.value })} />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">On voicemail</Label>
                          <Select value={createAmd.action} onValueChange={(v) => setCreateAmd({ ...createAmd, action: v })}>
                            <SelectTrigger data-testid="create-campaign-amd-action"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="hangup">Hang up</SelectItem>
                              <SelectItem value="voicemail">Leave voicemail</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Voicemail Max (ms)</Label>
                          <Input data-testid="create-campaign-amd-greeting-max" type="number" min="2000" max="12000" step="100" value={createAmd.greetingMax} onChange={(e) => setCreateAmd({ ...createAmd, greetingMax: e.target.value })} />
                          <p className="text-[11px] text-muted-foreground">A continuous greeting longer than this counts as voicemail. Higher = fewer real humans wrongly dropped (default 4500).</p>
                        </div>
                        {createAmd.action === "voicemail" && (
                          <div className="col-span-2 space-y-1.5 rounded-md border border-dashed border-border p-2.5">
                            <Label className="text-xs">Voicemail drop audio <span className="text-destructive">*</span></Label>
                            <input ref={createVmInputRef} type="file" accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.webm" hidden onChange={uploadVoicemailCreate} data-testid="create-campaign-vm-file-input" />
                            <div className="flex items-center gap-2">
                              <Button type="button" variant="outline" size="sm" className="rounded-lg" disabled={createVmUploading}
                                onClick={() => createVmInputRef.current?.click()} data-testid="create-campaign-vm-upload-btn">
                                {createVmUploading ? "Uploading…" : (createAmd.vmAudioId ? "Replace audio" : "Upload audio")}
                              </Button>
                              {createAmd.vmAudioId
                                ? <span className="text-xs text-emerald-600 truncate" data-testid="create-campaign-vm-filename">{createAmd.vmFilename || "Voicemail audio attached"}</span>
                                : <span className="text-xs text-muted-foreground">No file uploaded yet</span>}
                            </div>
                            <p className="text-[11px] text-muted-foreground">Played to the answering machine after the greeting, then the call hangs up. Use 8 kHz mono WAV for best telephony playback.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter className="shrink-0 border-t pt-3"><Button onClick={create} disabled={saving} data-testid="submit-campaign-btn" className="rounded-lg">{saving ? "Creating…" : "Create"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </PageHeader>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : campaigns.length === 0 ? (
          <EmptyState testid="campaigns-empty" icon={Megaphone} title="No campaigns yet"
            description={isCustomer ? "Create a campaign or adopt a Global Campaign, then design its flow and start it." : "Campaigns created by customers appear here."} />
        ) : isCustomer ? (
          <div className="rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="campaigns-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Routing</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Active Calls</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {campaigns.map((c) => (
                  <TableRow key={c.id} data-testid={`campaign-row-${c.id}`}>
                    <TableCell>
                      <div className="font-medium">{c.name}</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <FlowHealthBadge health={c.flow_health} id={c.id} />
                        {c.source === "global" && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-accent text-primary">Template</span>}
                        {c.archived_at && <span data-testid={`campaign-archived-badge-${c.id}`} className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">Archived</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {c.campaign_prefix && <span className="font-mono px-2 py-0.5 rounded bg-muted" data-testid={`campaign-prefix-${c.id}`}>Prefix {c.campaign_prefix}</span>}
                      {c.vicidial_server_name && <div className="text-muted-foreground mt-1">{c.vicidial_server_name}{c.vicidial_server_host ? ` · ${c.vicidial_server_host}` : ""}</div>}
                    </TableCell>
                    <TableCell><StatusPill status={c.status} /></TableCell>
                    <TableCell className="text-center">
                      <span data-testid={`campaign-active-calls-${c.id}`}
                            title="Live connected calls (across all AI engine servers)"
                            className={`inline-flex items-center gap-1.5 font-semibold tabular-nums ${c.active_calls > 0 ? "text-emerald-600" : "text-muted-foreground"}`}>
                        {c.active_calls > 0 && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}
                        {c.active_calls ?? 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {c.status !== "active" && !c.archived_at ? (
                          <Button size="sm" variant="outline" className="rounded-lg h-8 px-2 text-primary" disabled={busy === c.id} onClick={() => start(c)} data-testid={`start-campaign-${c.id}`} title="Start campaign" aria-label="Start campaign"><Play className="w-4 h-4" weight="fill" /></Button>
                        ) : c.status === "active" ? (
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive hover:text-destructive" disabled={busy === c.id} onClick={() => stop(c)} data-testid={`stop-campaign-${c.id}`} title="Stop campaign" aria-label="Stop campaign"><Stop className="w-4 h-4" weight="fill" /></Button>
                        ) : null}
                        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => openEdit(c)} data-testid={`edit-campaign-${c.id}`} title="Edit campaign" aria-label="Edit campaign"><PencilSimple className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => openDesigner(c)} data-testid={`design-campaign-${c.id}`} title="Design flow" aria-label="Design flow"><FlowArrow className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setAsterisk(c)} data-testid={`asterisk-server-${c.id}`} title="Asterisk Server setup" aria-label="Asterisk Server setup"><HardDrives className="w-4 h-4" /></Button>
                        {c.archived_at ? (
                          <Button size="sm" variant="ghost" className="h-8 px-2" disabled={busy === c.id} onClick={() => restore(c)} data-testid={`restore-campaign-${c.id}`} title="Restore" aria-label="Restore"><ArrowCounterClockwise className="w-4 h-4" /></Button>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-8 px-2" disabled={busy === c.id} onClick={() => setConfirm({ action: "archive", camp: c })} data-testid={`archive-campaign-${c.id}`} title="Archive" aria-label="Archive"><Archive className="w-4 h-4" /></Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive hover:text-destructive" disabled={busy === c.id} onClick={() => setConfirm({ action: "delete", camp: c })} data-testid={`delete-campaign-${c.id}`} title="Delete campaign" aria-label="Delete campaign"><Trash className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="campaigns-grid">
            {campaigns.map((c) => (
              <div key={c.id} className="rounded-xl border border-border bg-card card-shadow p-6" data-testid={`campaign-card-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">{c.name}</h3>
                    {c.source === "global" && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-accent text-primary mt-1 inline-block">From template</span>}
                    {!isCustomer && <p className="text-xs text-muted-foreground mt-0.5">{c.customer_name}{c.reseller_name ? ` · ${c.reseller_name}` : " · Direct"}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <FlowHealthBadge health={c.flow_health} id={c.id} />
                    {c.archived_at && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-muted text-muted-foreground" data-testid={`campaign-archived-badge-${c.id}`}>Archived</span>}
                    <StatusPill status={c.status} />
                  </div>
                </div>

                {c.campaign_prefix && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid={`campaign-routing-${c.id}`}>
                    <span className="font-mono px-2 py-0.5 rounded bg-muted" data-testid={`campaign-prefix-${c.id}`}>Prefix {c.campaign_prefix}</span>
                    {c.vicidial_server_name && <span className="text-muted-foreground">{c.vicidial_server_name}{c.vicidial_server_host ? ` · ${c.vicidial_server_host}` : ""}</span>}
                  </div>
                )}

                <div className="mt-3 flex items-center justify-between rounded-lg bg-muted/40 p-3">
                  <span className="text-sm text-muted-foreground">Active calls</span>
                  <span data-testid={`campaign-active-calls-mobile-${c.id}`}
                        title="Live connected calls (across all AI engine servers)"
                        className={`inline-flex items-center gap-1.5 font-semibold tabular-nums text-sm ${c.active_calls > 0 ? "text-emerald-600" : "text-muted-foreground"}`}>
                    {c.active_calls > 0 && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}
                    {c.active_calls ?? 0}
                  </span>
                </div>

                {isCustomer ? (
                  <>
                    <div className="mt-5 flex gap-2">
                      {c.status !== "active" && !c.archived_at && (
                        <Button size="sm" variant="outline" className="rounded-lg text-primary" disabled={busy === c.id} onClick={() => start(c)} data-testid={`start-campaign-${c.id}`}><Play className="w-4 h-4 mr-1.5" weight="fill" /> Start</Button>
                      )}
                      <Button size="sm" variant="outline" className="rounded-lg flex-1" onClick={() => openDesigner(c)} data-testid={`design-campaign-${c.id}`}><FlowArrow className="w-4 h-4 mr-1.5" weight="bold" /> Design flow</Button>
                      <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setTestCall(c)} data-testid={`test-call-${c.id}`}><Phone className="w-4 h-4 mr-1.5" weight="fill" /> Test Call</Button>
                      {c.status === "active" && !c.archived_at && (
                        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" disabled={busy === c.id} onClick={() => stop(c)} data-testid={`stop-campaign-${c.id}`}><Stop className="w-4 h-4 mr-1.5" weight="fill" /> Stop</Button>
                      )}
                      {c.archived_at ? (
                        <Button size="sm" variant="ghost" disabled={busy === c.id} onClick={() => restore(c)} data-testid={`restore-campaign-${c.id}`}><ArrowCounterClockwise className="w-4 h-4 mr-1.5" weight="bold" /> Restore</Button>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-lg" disabled={busy === c.id} onClick={() => setConfirm({ action: "archive", camp: c })} data-testid={`archive-campaign-${c.id}`}><Archive className="w-4 h-4 mr-1.5" weight="bold" /> Archive</Button>
                      )}
                    </div>
                  </>
                ) : null}

                <div className={`mt-4 grid ${c.reseller_id ? "grid-cols-2" : "grid-cols-1"} gap-3 text-sm`}>
                  <div className="rounded-lg border border-border p-3"><p className="text-xs text-muted-foreground">{c.reseller_id ? "Platform rate" : "Effective rate"}</p><p className="font-medium mt-0.5">{c.platform_rate_snapshot_minor != null ? money(c.platform_rate_snapshot_minor) : "—"}</p></div>
                  {c.reseller_id && <div className="rounded-lg border border-border p-3"><p className="text-xs text-muted-foreground">Reseller rate</p><p className="font-medium mt-0.5">{c.reseller_rate_snapshot_minor != null ? money(c.reseller_rate_snapshot_minor) : "—"}</p></div>}
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Browse global templates */}
      <Dialog open={browse} onOpenChange={setBrowse}>
        <DialogContent className="max-w-2xl" data-testid="browse-templates-dialog">
          <DialogHeader><DialogTitle>Global Campaigns</DialogTitle>
            <DialogDescription>Adopt a template — a private copy is created that you can customize.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 py-2 max-h-[60vh] overflow-y-auto">
            {templates.map((t) => (
              <div key={t.id} className="rounded-lg border border-border p-4" data-testid={`browse-template-${t.id}`}>
                {t.category && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-accent text-primary">{t.category}</span>}
                <h4 className="font-medium mt-2">{t.name}</h4>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
                <Button size="sm" className="rounded-lg w-full mt-3" onClick={() => adoptTemplate(t)} data-testid={`use-template-${t.id}`}>Use Campaign</Button>
              </div>
            ))}
            {templates.length === 0 && <p className="text-sm text-muted-foreground col-span-2">No published templates available yet.</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Flow designer for a customer's own campaign copy */}
      <Dialog open={!!designer} onOpenChange={(v) => !v && setDesigner(null)}>
        <DialogContent className="max-w-[95vw] w-[95vw]" data-testid="campaign-designer-dialog">
          <DialogHeader><DialogTitle>Design flow — {designer?.campaign?.name}{designer?.campaign?.campaign_prefix ? ` · Prefix ${designer.campaign.campaign_prefix}` : ""}</DialogTitle>
            <DialogDescription>Customize your own campaign flow. The original global template is never changed.</DialogDescription></DialogHeader>
          {designer && <FlowDesigner initial={designer.flow} onSave={saveFlow} saving={savingFlow} />}
        </DialogContent>
      </Dialog>

      {testCall && (
        <TestCallModal campaign={testCall} open={!!testCall} onClose={() => setTestCall(null)} />
      )}

      {asterisk && (
        <AsteriskWizard campaign={asterisk} open={!!asterisk} onClose={() => setAsterisk(null)} />
      )}

      {/* Copy / Delete confirmation */}
      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent data-testid="campaign-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirm?.action === "delete" ? "Delete campaign?" : confirm?.action === "archive" ? "Archive campaign?" : "Copy campaign?"}
            </DialogTitle>
            <DialogDescription>
              {confirm?.action === "delete"
                ? `This deletes "${confirm?.camp?.name}", stops it and releases any active holds. This cannot be undone.`
                : confirm?.action === "archive"
                ? `This archives "${confirm?.camp?.name}" and stops it. You can restore it anytime from "Show archived".`
                : `This creates a private copy of "${confirm?.camp?.name}" (flow and settings included).`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setConfirm(null)} data-testid="campaign-confirm-cancel">Cancel</Button>
            <Button
              className={`rounded-lg ${confirm?.action === "delete" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}`}
              onClick={runConfirm} data-testid="campaign-confirm-ok">
              {confirm?.action === "delete" ? "Delete campaign" : confirm?.action === "archive" ? "Archive campaign" : "Copy campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit campaign (customer) */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[90vh] flex flex-col" data-testid="edit-campaign-dialog">
          <DialogHeader className="shrink-0"><DialogTitle>Edit campaign</DialogTitle>
            <DialogDescription>Update the name and routing servers. The routing prefix stays the same.</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-4 py-2 overflow-y-auto flex-1 pr-1 -mr-1">
              <div className="space-y-2"><Label>Campaign name</Label>
                <Input data-testid="edit-campaign-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div className="space-y-2">
                <Label>VICIdial server <span className="text-destructive">*</span></Label>
                <Select value={editing.vicidialId} onValueChange={(v) => setEditing({ ...editing, vicidialId: v })}>
                  <SelectTrigger data-testid="edit-campaign-vicidial-select"><SelectValue placeholder="Select assigned VICIdial server" /></SelectTrigger>
                  <SelectContent>
                    {vicidial.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.host}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Telephony IP</Label>
                <Select value={editing.opensipsId} onValueChange={(v) => setEditing({ ...editing, opensipsId: v })}>
                  <SelectTrigger data-testid="edit-campaign-opensips-select"><SelectValue placeholder="Select assigned server" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Unassigned</SelectItem>
                    {assigned.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.host}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Max Call Duration (sec)</Label>
                <Input data-testid="edit-campaign-max-duration" type="number" min="1" max="3600" value={editing.maxDur} onChange={(e) => setEditing({ ...editing, maxDur: e.target.value })} placeholder="Unlimited" />
                <p className="text-xs text-muted-foreground">Caps the AI bot ↔ customer call. Leave blank for no limit. Not applied after transfer to an agent.</p>
              </div>

              <div className="space-y-3 rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Answering Machine Detection</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Detect human vs voicemail on the calls VICIdial couldn't classify.</p>
                  </div>
                  <Switch data-testid="edit-campaign-amd-enabled" checked={editing.amdEnabled} onCheckedChange={(v) => setEditing({ ...editing, amdEnabled: v })} />
                </div>
                {editing.amdEnabled && (
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Decision window (ms)</Label>
                      <Input data-testid="edit-campaign-amd-timeout" type="number" min="1000" max="8000" step="100" value={editing.amdTimeout} onChange={(e) => setEditing({ ...editing, amdTimeout: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">On voicemail</Label>
                      <Select value={editing.amdAction} onValueChange={(v) => setEditing({ ...editing, amdAction: v })}>
                        <SelectTrigger data-testid="edit-campaign-amd-action"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hangup">Hang up</SelectItem>
                          <SelectItem value="voicemail">Leave voicemail</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Voicemail Max (ms)</Label>
                      <Input data-testid="edit-campaign-amd-greeting-max" type="number" min="2000" max="12000" step="100" value={editing.amdGreetingMax} onChange={(e) => setEditing({ ...editing, amdGreetingMax: e.target.value })} />
                      <p className="text-[11px] text-muted-foreground">A continuous greeting longer than this counts as voicemail. Higher = fewer real humans wrongly dropped (default 4500).</p>
                    </div>
                    {editing.amdAction === "voicemail" && (
                      <div className="col-span-2 space-y-1.5 rounded-md border border-dashed border-border p-2.5">
                        <Label className="text-xs">Voicemail drop audio <span className="text-destructive">*</span></Label>
                        <input ref={vmInputRef} type="file" accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.webm" hidden onChange={uploadVoicemail} data-testid="edit-campaign-vm-file-input" />
                        <div className="flex items-center gap-2">
                          <Button type="button" variant="outline" size="sm" className="rounded-lg" disabled={vmUploading}
                            onClick={() => vmInputRef.current?.click()} data-testid="edit-campaign-vm-upload-btn">
                            {vmUploading ? "Uploading…" : (editing.vmAudioId ? "Replace audio" : "Upload audio")}
                          </Button>
                          {editing.vmAudioId
                            ? <span className="text-xs text-emerald-600 truncate" data-testid="edit-campaign-vm-filename">{editing.vmFilename || "Voicemail audio attached"}</span>
                            : <span className="text-xs text-muted-foreground">No file uploaded yet</span>}
                        </div>
                        <p className="text-[11px] text-muted-foreground">Played to the answering machine after the greeting, then the call hangs up. Use 8 kHz mono WAV for best telephony playback.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="shrink-0 border-t pt-3"><Button onClick={saveEdit} disabled={savingEdit} data-testid="submit-edit-campaign-btn" className="rounded-lg">{savingEdit ? "Saving…" : "Save changes"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
